import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Loader from '@components/loader/Loader'

import useReducer from '@store/useReducer'
import * as LocalisationStore from '@store/location/location'
import * as SiteStore from '@store/site/site'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'
import useNavigation from '@layout/useNavigation'

import sanitize from '@utils/Sanitizer'
import Logger from '@utils/logger'

const ITEM_WIDTH = 370
const DRAWER_WIDTH = 300

interface SpaceProps {
  space: LocalisationResponse
  stats?: ControleQualiteStatistiqueResponse
}

const SpaceCard = ({ space, stats }: SpaceProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  return (
    <Card activeOpacity={0.8} onPress={() => navigation.push(`/controles/details/infos?spaceId=${space.id}`)}>
      <Title>{space.libelle}</Title>
      {!!stats && !!stats.nombreControle ? (
        <ScoreContainer>
          <ScoreInfo>{i18n.t('screens.controles.controles')}</ScoreInfo>
          <Score>{Math.round(stats.nombreControle)}</Score>
        </ScoreContainer>
      ) : (
        <EmptyScoreContainer />
      )}
    </Card>
  )
}

const Space = () => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()

  const columns = bp === 'phone' ? 1 : Math.floor((width - DRAWER_WIDTH) / ITEM_WIDTH)

  const [search, debounced, setSearch] = useDebounce('')
  const [loading, setLoading] = React.useState(true)
  const [stats, setStats] = React.useState<Record<string, ControleQualiteStatistiqueResponse>>({})

  const site = useReducer(SiteStore.store, (s) => s.site)

  const places = useReducer(LocalisationStore.store, (s) => s.places)
  const spaces = React.useMemo(
    () =>
      places.filter((p) => !p.parentId && p.enabled).filter((p) => sanitize(p.libelle).includes(sanitize(debounced))),
    [places, debounced]
  )

  React.useEffect(() => {
    if (site) {
      api.controle.filtres
        .espace(site.id)
        .then((assos) =>
          Promise.all(assos.map((m) => api.controle.stats({ espaceId: m.id, siteId: site.id }))).then((res) => {
            const spacesStats = res.reduce(
              (acc, cur, idx) => ({ ...acc, [assos[idx].id]: cur }),
              {} as Record<string, ControleQualiteStatistiqueResponse>
            )

            setStats(spacesStats)
          })
        )
        .catch(Logger.error)
        .finally(() => setLoading(false))
    }
  }, [spaces, site])

  const renderItem = ({ item }: { item: LocalisationResponse }) => {
    const stat = stats[item.id]

    return <SpaceCard space={item} stats={stat} />
  }

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.spaces.search')}
          left={{ name: 'search' }}
          searchStyle="onLight"
        />
      </SearchContainer>

      {loading ? (
        <Loading>
          <Loader />
        </Loading>
      ) : spaces.length === 0 ? (
        <NoContent>{i18n.t('screens.controles.form.spaces.empty')}</NoContent>
      ) : (
        <FlatList
          key={columns}
          data={spaces}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          numColumns={columns}
          contentContainerStyle={{ paddingHorizontal: bp === 'phone' ? 16 : 24 }}
          extraData={stats}
        />
      )}
    </MainContainer>
  )
}

export default Space

const MainContainer = styled(View)`
  flex: 1;
`

const Loading = styled(View)`
  flex: 1;
  justify-content: center;
  align-items: center;
`

const SearchContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const Card = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px;`}
  margin: 8px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-right: 16px;'}
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  flex: 1;
`

const ScoreContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 8px 5px;
  margin-left: 16px;
  align-items: center;
  height: 56px;
`

const EmptyScoreContainer = styled(View)`
  height: 56px;
`

const ScoreInfo = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Score = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`
